import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Loader2, Check, AlertCircle, Flame, Sparkles, Microscope, Video, User, TrendingUp, Hash, Zap } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";
import { logger } from "../lib/logger";
import SocialProofTicker from "../components/SocialProofTicker";

const FEATURES = [
  { i: User, t: "Unlimited Voice Clone — writes everything in your voice" },
  { i: TrendingUp, t: "Trend Radar + Daily Ideas — every morning, in your voice" },
  { i: Microscope, t: "Unlimited Viral Autopsy + Roast My Copy" },
  { i: Hash, t: "Unlimited hashtag + caption generation" },
  { i: Video, t: "Sora 2 video generation (when you fund fal.ai)" },
  { i: Sparkles, t: "5,000 credits up front, refills if you ever run low" },
  { i: Zap, t: "Skip every future paywall — forever." },
];

export default function FoundersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/packages");
        const f = data.find((p) => p.id === "founders_lifetime");
        setPkg(f || null);
      } catch (e) {
        logger.error("packages error", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const buy = async () => {
    if (!user) {
      nav("/register?ref=founders");
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post("/checkout", { package_id: "founders_lifetime", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      logger.error("checkout error", e);
      toast.error(formatError(e.response?.data?.detail) || "Checkout failed");
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
      </div>
    );
  }

  const seats = pkg?.seats_remaining ?? 100;
  const sold = 100 - seats;
  const soldOut = pkg?.sold_out;

  return (
    <div className="min-h-screen bg-zinc-950 text-white relative overflow-hidden" data-testid="founders-page">
      <SocialProofTicker />
      {/* grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,47,167,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(0,47,167,0.08)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="relative max-w-3xl mx-auto px-6 py-12 md:py-20">
        <button onClick={() => nav("/")} className="text-zinc-500 text-sm font-semibold hover:text-white mb-8">
          ← back
        </button>

        <div className="inline-flex items-center gap-2 bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 px-3 py-1 text-xs font-bold tracking-wider uppercase mb-6">
          <Crown className="w-3.5 h-3.5" /> Limited · First 100 only
        </div>

        <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-[1.05] mb-4 font-heading">
          Founders<br />
          <span className="text-brand">Lifetime.</span>
        </h1>

        <p className="text-xl text-zinc-400 leading-relaxed mb-8 max-w-xl">
          One payment. Forever access. Skip every monthly bill, every paywall, every "you've run out of credits" wall — for the rest of CopyForge's life.
        </p>

        <div className="bg-zinc-900 border border-zinc-800 p-6 md:p-8 mb-6">
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-6xl font-black tracking-tighter text-white font-heading">$97</span>
            <span className="text-zinc-500 line-through text-lg">$588/yr</span>
          </div>
          <p className="text-sm text-zinc-400 mb-6">One-time. Pay once. Use forever. Includes everything in the Creator tier ($49/mo retail) plus an immediate 5,000-credit grant.</p>

          <ul className="space-y-3 mb-8">
            {FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-brand flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
                <span className="text-zinc-200 text-sm leading-relaxed">{f.t}</span>
              </li>
            ))}
          </ul>

          <div className="mb-5">
            <div className="flex items-center justify-between text-xs font-bold tracking-wider uppercase mb-2">
              <span className="text-zinc-500">{sold} of 100 claimed</span>
              <span className="text-yellow-400 flex items-center gap-1">
                <Flame className="w-3 h-3" /> {seats} left
              </span>
            </div>
            <div className="w-full h-2 bg-zinc-800">
              <div
                className="h-full bg-gradient-to-r from-brand to-yellow-400 transition-all"
                style={{ width: `${(sold / 100) * 100}%` }}
              />
            </div>
          </div>

          {soldOut ? (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 p-4 text-center" data-testid="founders-sold-out">
              <AlertCircle className="w-5 h-5 mx-auto mb-1" />
              <p className="font-bold">All 100 seats claimed.</p>
              <button onClick={() => nav("/pricing")} className="text-brand text-sm font-semibold mt-2 hover:underline">
                See subscription plans →
              </button>
            </div>
          ) : (
            <button
              onClick={buy}
              disabled={buying}
              data-testid="founders-buy-btn"
              className="w-full bg-gradient-to-r from-brand to-blue-500 text-white font-black text-lg py-5 hover:from-brand-hover hover:to-blue-600 disabled:opacity-50 flex items-center justify-center gap-2 group"
            >
              {buying ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to Stripe…</>
              ) : (
                <>
                  <Crown className="w-5 h-5" />
                  Claim my Lifetime spot — $97
                  <span className="text-white/60 text-xs ml-2">·  one-time</span>
                </>
              )}
            </button>
          )}
          <p className="text-xs text-zinc-500 text-center mt-3">
            Secure checkout via Stripe · refund within 14 days if you don't ship a single post you love
          </p>
        </div>

        <div className="text-center text-sm text-zinc-500 leading-relaxed">
          After 100 seats: Creator monthly = <span className="text-white font-semibold">$49/mo</span>.<br />
          That means month-13 of a regular subscription costs more than this entire offer.
        </div>
      </div>
    </div>
  );
}
